import React, { useState } from 'react';
import { Sparkles, ArrowRight } from 'lucide-react';
import BookingCalendar from './BookingCalendar';

const MODALITIES = [
  {
    title: 'Deep Tissue',
    tagline: 'Targeted. Powerful. Restorative.',
    description:
      'Releases chronic muscle tension and stubborn knots using slow, deep pressure that reaches the inner layers of muscle and connective tissue. Ideal for athletes, desk workers and anyone carrying long-held tension in the neck, shoulders or back.',
    image: '/massage-deep-tissue.jpg',
    service: { id: 4, name: '60min Deep Tissue', duration: '1 hour', price: 120, category: 'deep-tissue' },
  },
  {
    title: 'Relaxing Stress Relief',
    tagline: 'Gentle. Soothing. Calming.',
    description:
      'A soothing, flowing massage designed to melt away anxiety and stress. Gentle rhythmic strokes improve circulation, calm the nervous system and guide you into a state of deep tranquility — the perfect escape from a busy week.',
    image: '/massage-relaxing.jpg',
    service: { id: 5, name: '60min Relaxing', duration: '1 hour', price: 120, category: 'relaxing' },
  },
  {
    title: 'Lomi Lomi Massage',
    tagline: 'Flowing. Rhythmic. Hawaiian.',
    description:
      'A traditional Hawaiian healing technique using long, continuous strokes of the hands and forearms that mimic the rhythm of ocean waves. Lomi Lomi works the body as a whole, restoring harmony, energy and a profound sense of wellbeing.',
    image: '/massage-lomi-lomi.jpg',
    service: { id: 5, name: '60min Lomi Lomi Massage', duration: '1 hour', price: 120, category: 'relaxing' },
  },
  {
    title: 'Trigger Point Shiatsu',
    tagline: 'Precise. Ancient. Effective.',
    description:
      'Combines ancient Japanese finger-pressure techniques with modern trigger point therapy. Focused pressure on specific points releases referred pain, unblocks natural energy flow and restores balance — excellent for headaches and persistent aches.',
    image: '/massage-shiatsu.jpg',
    service: { id: 4, name: '60min Trigger Point Shiatsu', duration: '1 hour', price: 120, category: 'deep-tissue' },
  },
];

const SignatureMassages = () => {
  const [bookingService, setBookingService] = useState(null);

  return (
  <>
  <section id="signature-massages" className="py-28 bg-luxury-green-800 relative overflow-hidden" data-testid="signature-massages-section">
    <div className="absolute top-0 right-0 w-96 h-96 bg-luxury-gold/5 rounded-full blur-3xl" />
    <div className="absolute bottom-0 left-0 w-96 h-96 bg-luxury-green-600/30 rounded-full blur-3xl" />

    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative">
      <div className="max-w-3xl mb-16">
        <span className="inline-flex items-center gap-2 text-luxury-gold text-xs uppercase tracking-[0.3em] font-medium">
          <Sparkles size={14} />
          Our Signature Massages
        </span>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-alabaster-100 mt-4 mb-4">
          Four Treatments,
          <span className="block italic text-luxury-gold mt-1">One Goal — Your Wellbeing</span>
        </h2>
        <p className="text-alabaster-300 font-light leading-relaxed max-w-xl">
          Every body is different. Our therapists master four distinct massage styles so your treatment
          fits exactly what your body needs.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {MODALITIES.map((m) => (
          <div
            key={m.title}
            className="group bg-luxury-green-900/60 backdrop-blur-sm rounded-3xl overflow-hidden border border-luxury-gold/20 hover:border-luxury-gold/60 hover:-translate-y-1 transition-[transform,border-color] duration-300 shadow-[0_8px_30px_rgba(15,41,34,0.3)]"
            data-testid={`signature-massage-${m.title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="relative h-44 overflow-hidden">
              <img
                src={m.image}
                alt={`${m.title} massage`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-luxury-green-900/80 to-transparent" />
              <p className="absolute bottom-3 left-4 text-luxury-gold text-[11px] uppercase tracking-[0.2em] font-medium">{m.tagline}</p>
            </div>
            <div className="p-6 flex flex-col">
              <h3 className="font-display text-xl text-alabaster-100 mb-3">{m.title}</h3>
              <p className="text-alabaster-300 text-sm font-light leading-relaxed mb-5">{m.description}</p>
              <button
                onClick={() => setBookingService(m.service)}
                className="mt-auto inline-flex items-center justify-center gap-2 bg-luxury-gold hover:bg-luxury-gold-600 text-luxury-green-900 rounded-full px-5 py-2.5 text-xs uppercase tracking-wider font-semibold transition-colors group/btn"
                data-testid={`book-signature-${m.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                Book This Massage
                <ArrowRight size={14} className="group-hover/btn:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>

  <BookingCalendar
    isOpen={!!bookingService}
    onClose={() => setBookingService(null)}
    initialService={bookingService}
  />
  </>
  );
};

export default SignatureMassages;
